import { Injectable } from '@angular/core';
import { AuthService, LogoutOptions } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  interval,
  map,
  of,
  switchMap,
  take
} from 'rxjs';
import jwt_decode from 'jwt-decode';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { SegmentSchema, SegmentService } from '@sondermind/segment';

const AUTH0_ACCESS_DENIED_ERRORS = ['access_denied', 'hs_context_invalid'];
const HEADSPACE_CONTEXT_CLAIM = 'https://www.headspace.com/context';
interface ITokenInfo {
  sub: string;
  meta: {
    authsvc_account_id: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class Auth0TokenService {
  auth0TokenBehaviorSubject$ = new BehaviorSubject<string>('');
  private auth0EnabledBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private isFirstLoginFromAuth0Signup = new BehaviorSubject<boolean>(false);

  heartbeatSubscription: Subscription | null = null;
  auth0Enabled$ = this.auth0EnabledBehaviorSubject$.asObservable();
  auth0Token$ = this.auth0TokenBehaviorSubject$.asObservable();
  authSuccess$ = new BehaviorSubject<boolean>(false);
  isFirstLoginFromAuth0Signup$ = this.isFirstLoginFromAuth0Signup.asObservable();

  constructor(
    private auth0: AuthService,
    private logger: Logger,
    private configService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>
  ) {
    this.auth0.error$.subscribe((err: Error) => {
      this.logger.error(`Auth0 error: ${err.message}`);
      if (AUTH0_ACCESS_DENIED_ERRORS.includes(err.message)) {
        this.logout();
      }
    });
  }

  get auth0Enabled(): boolean {
    return this.auth0EnabledBehaviorSubject$.getValue();
  }

  initializeAuth0(): Observable<string | null> {
    return combineLatest([this.auth0.isLoading$, this.auth0.isAuthenticated$]).pipe(
      filter(([isLoading]) => !isLoading),
      map(([, isAuthenticated]) => isAuthenticated),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          // Check if SSO is in flight to avoid conflicts
          // Currently this overides the SSO Guard call to #loginWithRedirect to headspace sso
          const isSsoInFlight = sessionStorage.getItem('sso_in_flight') === '1';
          const hasSSOParams = window.location.search.includes('sso=')
           || window.location.search.includes('error=login_required');

          if (isSsoInFlight || hasSSOParams) {
            return of(null as string | null);
          }
          // not logged in yet → kick off redirect, emit null
          return from(
            this.auth0.loginWithRedirect({
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace
              }
            })
          ).pipe(map(() => null as string | null));
        }
        // user is authenticated → get token but don't trigger refresh
        // Use cacheMode: 'on' to avoid triggering refresh token flow
        return from(
          firstValueFrom(
            this.auth0.getAccessTokenSilently({
              cacheMode: 'on',
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace
              }
            })
          )
        );
      }),
      switchMap(async(token) => {
        if (token) {
          this.auth0TokenBehaviorSubject$.next(token);
          const decoded: ITokenInfo = jwt_decode(token);
          this.segmentService.identifyUser(decoded.meta.authsvc_account_id);

          try {
            const hasProcessedClaims = sessionStorage.getItem('hs_redirected_to');

            if (!hasProcessedClaims) {
              const idToken = await firstValueFrom(
                this.auth0.idTokenClaims$.pipe(
                  filter((claims) => claims !== null && claims !== undefined),
                  take(1)
                )
              );

              // This should be just a path something like /dashboard
              const headspaceRedirectIntent = idToken?.[HEADSPACE_CONTEXT_CLAIM];

              if (headspaceRedirectIntent && this.isValidRedirectPath(headspaceRedirectIntent)) {
                sessionStorage.setItem('hs_redirected_to', headspaceRedirectIntent);
                window.location.href = headspaceRedirectIntent;
                return null;
              }
            }
          } catch (error) {
            this.logger.error(`Failed to get ID token claims: ${error}`);
          }
        }
        this.auth0EnabledBehaviorSubject$.next(true);
        return token;
      })
    );
  }

  setAuth0Success(): void {
    this.authSuccess$.next(true);
  }

  getAuth0Success(): boolean {
    return this.authSuccess$.getValue();
  }

  logout(logoutOptions: LogoutOptions = { logoutParams: { returnTo: window.location.origin } }): Observable<void> {
    return this.auth0.logout(logoutOptions);
  }

  startHeartbeat(heartbeatInterval: number = 14 * 60 * 1000): void {
    if (!this.auth0Enabled) return;

    this.stopHeartbeat();

    this.heartbeatSubscription = interval(heartbeatInterval).pipe(
      switchMap(() => this.auth0.getAccessTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          scope: 'offline_access',
          eeNamespace: this.configService.env.eeNamespace
        }
      })
      )
    ).subscribe({
      next: () => this.logger.info('Token refreshed via heartbeat'),
      error: (err) => this.logger.error('Heartbeat token refresh failed:', err)
    });
  }

  stopHeartbeat(): void {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
    }
  }

  setFirstLoginFromAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(true);
  }

  completeFirstTimeAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(false);
  }

  private isValidRedirectPath(path: string): boolean {
    if (!path.startsWith('/')) {
      return false;
    }
    const allowedPaths = ['/dashboard', '/profile', '/appointments'];

    return allowedPaths.some((allowedPath) => path.startsWith(allowedPath));
  }
}
